import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink, Plus } from 'lucide-react';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardHeader } from '@/components/ui/Card';
import { Checkbox } from '@/components/ui/Checkbox';
import { EmptyState } from '@/components/ui/EmptyState';
import { useCurrentWorkspace } from '@/lib/workspace-context';
import { ROUTES } from '@/lib/routes';
import { useOutreachAgents } from '@/pages/outreach/api';
import { OutreachAgentFormDialog } from '@/pages/outreach/OutreachAgentFormDialog';
import { linkedInMeta, statusMeta } from '@/pages/outreach/mockData';
import { useWizardStore } from '../lib/wizard-store';

export function W4_OutreachAgents() {
  const { currentWorkspace } = useCurrentWorkspace();
  const wsId = currentWorkspace?.id;
  const agentsQuery = useOutreachAgents(wsId);
  const agents = agentsQuery.data?.items ?? [];
  const { outreach_agent_ids, toggleOutreachAgent } = useWizardStore();
  const [showCreate, setShowCreate] = useState(false);

  return (
    <Card>
      <CardHeader
        title="Agents LinkedIn Outreach"
        subtitle="Selectionne les agents OpenOutreach a assigner automatiquement apres creation de la campagne."
        action={
          <Button size="sm" onClick={() => setShowCreate(true)}>
            <Plus size={14} /> Creer un agent
          </Button>
        }
      />
      <CardContent className="space-y-4">
        {!wsId && <div className="text-sm text-gray-500">Selectionne un workspace pour charger les agents.</div>}

        {wsId && agentsQuery.isLoading && (
          <div className="text-sm text-gray-500">Chargement des agents Outreach...</div>
        )}

        {wsId && !agentsQuery.isLoading && agents.length === 0 && (
          <EmptyState
            title="Aucun agent disponible"
            description="Tu peux creer un agent maintenant, puis le selectionner pour cette campagne."
            action={
              <Button onClick={() => setShowCreate(true)}>
                <Plus size={14} /> Creer un agent
              </Button>
            }
          />
        )}

        {agents.length > 0 && (
          <div className="space-y-3">
            {agents.map((agent) => {
              const status = statusMeta[agent.status];
              const linkedIn = linkedInMeta[agent.linkedin_status];
              const selected = outreach_agent_ids.includes(agent.id);
              return (
                <div
                  key={agent.id}
                  className="flex items-start justify-between gap-4 rounded-brand border border-brand-border px-4 py-3"
                >
                  <Checkbox
                    id={`wizard-outreach-agent-${agent.id}`}
                    checked={selected}
                    onChange={() => toggleOutreachAgent(agent.id)}
                    label={agent.name}
                    description={`${agent.owner_name ?? 'A attribuer'} - ${agent.proxy_country ?? 'proxy non configure'}`}
                  />
                  <div className="flex flex-wrap items-center justify-end gap-2">
                    <Badge tone={status.tone}>{status.label}</Badge>
                    <Badge tone={linkedIn.tone}>LinkedIn {linkedIn.label}</Badge>
                    <Link
                      to={ROUTES.OUTREACH_AGENT_DETAIL(agent.id)}
                      target="_blank"
                      className="btn-ghost text-xs"
                    >
                      <ExternalLink size={12} /> Detail
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div className="rounded-brand bg-gray-50 border border-brand-border px-4 py-3 text-xs text-gray-600">
          {outreach_agent_ids.length} agent(s) seront assignes a la campagne. Si tu n'en selectionnes aucun,
          la campagne sera creee normalement et tu pourras assigner les agents depuis le cockpit Outreach.
        </div>
      </CardContent>

      <OutreachAgentFormDialog
        open={showCreate}
        onClose={() => setShowCreate(false)}
        workspaceId={wsId}
        onSaved={(agent) => {
          if (!outreach_agent_ids.includes(agent.id)) {
            toggleOutreachAgent(agent.id);
          }
        }}
      />
    </Card>
  );
}
